import type { NavItems } from '@ksyos/design-system';
import { SideNav } from '@ksyos/design-system';
import { sortBy } from 'lodash';
import { memo, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { EpisodeCategory } from '@common/enums';
import type { IServicesByServiceGroupForEpisodeCategory } from '@common/interfaces/servicesByServiceGroupForEpisodeCategory';
import type { IReduxState } from '@frontend/configureStore';
import { ManagerAuthorization, ServiceType } from '@common/enums';
import { PractitionerRole } from '@common/enums/practitionerRole';
import { SideNavItem } from '@frontend/components/elements/navigation/components/SideNavItem';
import { translateKey } from '@frontend/translate';
import { getCarePathMenuItems, hasOneAuthorization, useAbortableFetch } from '@frontend/utils';
import { useUiStateContext } from '@frontend/utils/hooks/useUiState';
import type { IServiceConsumer } from '../../interfaces/serviceConsumer';
import type { IServiceOrganizationSettings } from '../../interfaces/serviceOrganizationSettings';
import type { IOrganizationWaitingTimesAccess } from './utils/waitingTimesUtils';
import { hasOrganizationEpisodeCategories } from '../../elements/EpisodeCategoryButtonRow';

export type OrganizationNavigationProps = {
    organizationId: string;
    organizationWaitingTimesAccess: IOrganizationWaitingTimesAccess;
};

export const OrganizationNavigation = memo(function OrganizationNavigation(props: OrganizationNavigationProps) {
    const abortableFetch = useAbortableFetch();
    const [navigationItems, setNavigationItems] = useState<NavItems[]>([]);
    const employmentId = useSelector((state: IReduxState) => state.authentication.selectedEmploymentId);
    const [serviceGroupsByEpisodeCategory, setServiceGroupsByEpisodeCategory] = useState<
        IServicesByServiceGroupForEpisodeCategory[] | undefined
    >();
    const { uiState, dispatchUiStateAction } = useUiStateContext();
    const [practitionerIsOrganizationPayoutManager, setPractitionerIsOrganizationPayoutManager] =
        useState<boolean>(false);

    const handleClose = useCallback(() => {
        if (uiState.isTopBarOpen) {
            dispatchUiStateAction({ isTopBarOpen: false, type: 'setIsTopBarOpen' });
        }
    }, [uiState.isTopBarOpen, dispatchUiStateAction]);

    const getServiceGroupsByEpisodeCategory = useCallback(async () => {
        if (!props.organizationId) {
            return;
        }

        const result = await abortableFetch.fetchJson(
            `/api/serviceGroupMemberships?organizationId=${props.organizationId}&groupByEpisodeCategory=true`,
            'GET'
        );
        setServiceGroupsByEpisodeCategory(result.json.items);
    }, [props.organizationId, abortableFetch]);

    const loadPractitionerIsOrganizationPayoutManager = useCallback(async () => {
        if (!employmentId) {
            return;
        }

        const url = `/api/employments/${employmentId}/hasPractitionerRole?practitionerRole=${PractitionerRole.organizationPayoutManager}`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        setPractitionerIsOrganizationPayoutManager(result.json.hasPractitionerRole);
    }, [employmentId, abortableFetch]);

    const getEpisodeCategoriesFromServiceOrganizationSettings = useCallback(async () => {
        if (!props.organizationId) {
            return [];
        }

        let episodeCategories: string[] = [];
        const result = await abortableFetch.fetchJson(
            `/api/serviceOrganizationSettings?organizationId=${props.organizationId}&excludeServicesWithoutServiceGroup=true`,
            'GET'
        );
        if (result.json?.items?.length > 0) {
            episodeCategories = result.json.items
                .filter((item: IServiceOrganizationSettings) => item.service.serviceType !== ServiceType.other)
                .map((item: IServiceOrganizationSettings) => item.service.episodeCategory?.name);
        }

        return [...new Set(episodeCategories)].sort();
    }, [props.organizationId, abortableFetch]);

    const getEpisodeCategoriesForPreferenceMenu = useCallback(async () => {
        if (!props.organizationId) {
            return [];
        }

        let episodeCategories: EpisodeCategory[] = [];
        const result = await abortableFetch.fetchJson(
            `/api/serviceConsumers?organizationId=${props.organizationId}&isActive=true`,
            'GET'
        );

        if (result.json?.items?.length > 0) {
            episodeCategories = result.json.items
                .filter((item: IServiceConsumer) => {
                    return (
                        item.service.serviceType &&
                        ([ServiceType.referral, ServiceType.consultation].includes(item.service.serviceType) ||
                            (item.service.serviceType === ServiceType.examination && item.isRequestCreator))
                    );
                })
                .map((item: IServiceOrganizationSettings) => item.service.episodeCategory?.name);
        }

        return [...new Set(episodeCategories)].sort();
    }, [props.organizationId, abortableFetch]);

    const createConsumerPreferenceNavigationItemsList = useCallback(async (): Promise<NavItems[]> => {
        const episodeCategories = await getEpisodeCategoriesForPreferenceMenu();

        return episodeCategories.map((episodeCategory) => {
            const label = translateKey('episodeCategoryEnum.' + episodeCategory);
            const key = `preferenceList-${episodeCategory}-provider`;
            const to = `/organizations/${props.organizationId}/view/preferenceList/${episodeCategory}`;

            return { key, label, to, variant: 'link' as const };
        });
    }, [props.organizationId, getEpisodeCategoriesForPreferenceMenu]);

    const getConsumerPreferenceNavigationItems = useCallback(async (): Promise<NavItems[]> => {
        let episodeCategoriesServiceConsumers = await createConsumerPreferenceNavigationItemsList();

        if (!episodeCategoriesServiceConsumers.length) {
            return [];
        }

        episodeCategoriesServiceConsumers = sortBy(episodeCategoriesServiceConsumers, (item) => item.label);

        return [
            {
                items: episodeCategoriesServiceConsumers,
                key: 'preferenceList',
                label: translateKey('personalSettings.preferenceList.preferenceList'),
                variant: 'button' as const,
            },
        ];
    }, [createConsumerPreferenceNavigationItemsList]);

    const getFundingNavigationItems = useCallback(
        (episodeCategories: string[]): NavItems[] => {
            if (episodeCategories.length === 0) {
                return [];
            }
            const items: NavItems[] = episodeCategories.map((episodeCategory: string) => {
                return {
                    key: 'funding_' + episodeCategory,
                    label: translateKey(`episodeCategoryEnum.${episodeCategory}`),
                    to: `/organizations/${props.organizationId}/view/funding/${episodeCategory}`,
                    variant: 'link' as const,
                };
            });

            items.sort((a, b) => (a.label < b.label ? -1 : 1));

            return [
                {
                    items,
                    key: 'funding',
                    label: translateKey('common.funding'),
                    variant: 'button' as const,
                },
            ];
        },
        [props.organizationId]
    );

    const getCompensationNavigationItems = useCallback(
        (episodeCategories: string[]): NavItems[] => {
            if (episodeCategories.length === 0) {
                return [];
            }
            const items: NavItems[] = [];
            for (const episodeCategory of episodeCategories) {
                const episodeCategoryServiceGroups = serviceGroupsByEpisodeCategory?.find(
                    (item: IServicesByServiceGroupForEpisodeCategory) => item.episodeCategory.name === episodeCategory
                );
                if (episodeCategoryServiceGroups?.serviceGroups?.length) {
                    items.push({
                        key: 'compensations_' + episodeCategory,
                        label: translateKey(`episodeCategoryEnum.${episodeCategory}`),
                        to: `/organizations/${props.organizationId}/view/compensationPayouts/${episodeCategory}`,
                        variant: 'link' as const,
                    });
                }
            }

            items.sort((a, b) => (a.label < b.label ? -1 : 1));
            if (items.length) {
                return [
                    {
                        items,
                        key: 'compensationPayouts',
                        label: translateKey('compensationPayouts.fees'),
                        variant: 'button' as const,
                    },
                ];
            }

            return [];
        },
        [props.organizationId, serviceGroupsByEpisodeCategory]
    );

    const getNavigationItems = useCallback(async () => {
        const subItems: NavItems[] = [];
        const items: NavItems[] = [
            {
                items: subItems,
                key: 'organization',
                label: translateKey('organization.organization'),
                to: `/organizations/${props.organizationId}/view`,
                variant: 'link' as const,
            },
        ];

        if (
            props.organizationWaitingTimesAccess.hasWaitingTimes &&
            (props.organizationWaitingTimesAccess.canEditWaitingTimes ||
                props.organizationWaitingTimesAccess.canReadWaitingTimes)
        ) {
            subItems.push({
                key: 'waitingTimes',
                label: translateKey('waitingTime.waitingTimes'),
                to: `/organizations/${props.organizationId}/view/waitingTimes`,
                variant: 'link' as const,
            });
        }

        if (hasOneAuthorization(ManagerAuthorization.organizationRead)) {
            if (await hasOrganizationEpisodeCategories(props.organizationId)) {
                subItems.push({
                    key: 'serviceEmailNotifications',
                    label: translateKey('organization.notifications.notifications'),
                    to: `/organizations/${props.organizationId}/view/serviceEmailNotifications`,
                    variant: 'link' as const,
                });
            }

            subItems.push({
                key: 'organizationDetails',
                label: translateKey('organization.details'),
                to: `/organizations/${props.organizationId}/view/details`,
                variant: 'link' as const,
            });

            subItems.push(
                ...(await getCarePathMenuItems({
                    organizationId: props.organizationId,
                    pathname: `/organizations/${props.organizationId}/view`,
                }))
            );

            const providedEpisodeCategories = await getEpisodeCategoriesFromServiceOrganizationSettings();

            subItems.push(...getCompensationNavigationItems(providedEpisodeCategories));
            subItems.push(...getFundingNavigationItems(providedEpisodeCategories));
            subItems.push(...(await getConsumerPreferenceNavigationItems()));

            subItems.push({
                key: 'organizationPatientCount',
                label: translateKey('organization.patientCount'),
                to: `/organizations/${props.organizationId}/view/patientCount`,
                variant: 'link' as const,
            });
        }
        if (practitionerIsOrganizationPayoutManager) {
            subItems.push({
                key: 'organizationDetails',
                label: translateKey('organization.details'),
                to: `/organizations/${props.organizationId}/view/details`,
                variant: 'link' as const,
            });

            subItems.push({
                key: 'organizationPayouts',
                label: translateKey('finance.payments'),
                to: `/organizations/${props.organizationId}/view/organizationPayouts`,
                variant: 'link' as const,
            });
        }
        setNavigationItems(items);
    }, [
        props.organizationId,
        props.organizationWaitingTimesAccess,
        practitionerIsOrganizationPayoutManager,
        getCompensationNavigationItems,
        getFundingNavigationItems,
        getConsumerPreferenceNavigationItems,
        getEpisodeCategoriesFromServiceOrganizationSettings,
    ]);

    useEffect(() => {
        if (props.organizationId) {
            getServiceGroupsByEpisodeCategory();
            loadPractitionerIsOrganizationPayoutManager();
        }
    }, [props.organizationId, getServiceGroupsByEpisodeCategory, loadPractitionerIsOrganizationPayoutManager]);

    useEffect(() => {
        if ((serviceGroupsByEpisodeCategory || practitionerIsOrganizationPayoutManager) && props.organizationId) {
            getNavigationItems();
        }
    }, [
        props.organizationId,
        serviceGroupsByEpisodeCategory,
        practitionerIsOrganizationPayoutManager,
        props.organizationWaitingTimesAccess,
        getNavigationItems,
    ]);

    if (!props.organizationId || navigationItems.length === 0) {
        return null;
    }

    return (
        <SideNav>
            {navigationItems.map((item) => (
                <SideNavItem
                    defaultOpenKey="organization"
                    item={item}
                    key={item.key}
                    onClose={handleClose}
                />
            ))}
        </SideNav>
    );
});
